import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { ModalService } from '@app/core/services/modal.service';
import { AiProvider, AiProviderError, AiProviderName, AiProviderService, AiSettingsGet } from '@app/services/ai-provider.service';

@Component({
    selector: 'tt-ai-settings-providers-setup',
    templateUrl: './ai-settings-providers-setup.component.html',
    styleUrls: ['./ai-settings-providers-setup.component.css'],
    standalone: false,
})
export class AiSettingsProvidersSetupComponent implements OnChanges, OnDestroy {
    @Input()
    public page!: AiSettingsGet;

    @Input()
    public active = false;

    @Output()
    public hasProvidersChange = new EventEmitter<boolean>();

    public providerNames: AiProviderName[] = [];

    public providers: AiProviderSelection[] = [];

    public replacementApiKey = '';

    public loading = false;

    public creating = false;

    public replacing = false;

    public deleting = false;

    public errorMessage = '';

    public translateErrorMessage = false;

    public statusMessage = '';

    public get busy(): boolean {
        return this.loading || this.creating || this.replacing || this.deleting;
    }

    constructor(private readonly aiProvider: AiProviderService, private readonly modal: ModalService) {}

    public async createProvider(): Promise<void> {
        if (this.busy || !this.providerNames.some((provider) => provider.ai_provider_id === this.page.ai_provider_id) || !this.page.api_key_label.trim() || !this.page.api_key.trim()) {
            return;
        }

        this.creating = true;
        this.clearMessages();

        try {
            const provider = await this.aiProvider.createProvider(this.page);
            this.page.api_key_label = '';
            this.page.api_key = '';
            this.statusMessage = 'ai_settings_provider_created';
            try {
                await this.aiProvider.fetchProviderModels({ ai_provider_keyno: provider.ai_provider_keyno });
            } catch (error: unknown) {
                this.showError(error);
            }
            await this.reloadProviders();
        } catch (error: unknown) {
            this.showError(error);
        } finally {
            this.page.api_key = '';
            this.creating = false;
        }
    }

    public onProviderChanged(): void {
        this.replacementApiKey = '';
        this.clearMessages();
    }

    public selectProvider(params: AiProvider): void {
        if (this.busy) {
            return;
        }

        const providerKeyno = String(params.ai_provider_keyno);
        this.page.ai_provider_keyno = this.page.ai_provider_keyno === providerKeyno ? '' : providerKeyno;
        this.onProviderChanged();
    }

    public async replaceApiKey(params: AiProvider): Promise<void> {
        if (this.busy || !this.replacementApiKey.trim()) {
            return;
        }

        this.replacing = true;
        this.clearMessages();

        try {
            await this.aiProvider.replaceProvider({ ...params, api_key: this.replacementApiKey });
            this.statusMessage = 'ai_settings_api_key_replaced';
            await this.reloadProviders();
        } catch (error: unknown) {
            this.showError(error);
        } finally {
            this.replacementApiKey = '';
            this.replacing = false;
        }
    }

    public async deleteProvider(params: AiProvider): Promise<void> {
        if (this.busy) return;

        this.deleting = true;
        this.clearMessages();

        try {
            const confirmed = await this.modal.openConfirmDialog({ type: 'danger', title: `${params.ai_provider_name} — ${params.api_key_label}`, translateTitle: false, message: 'ai_settings_confirm_delete_provider', translateMessage: true });

            if (!confirmed) return;

            await this.aiProvider.deleteProvider({ ai_provider_keyno: params.ai_provider_keyno });

            this.page.ai_provider_keyno = '';
            this.replacementApiKey = '';
            this.statusMessage = 'ai_settings_provider_deleted';
            await this.reloadProviders();
        } catch (error: unknown) {
            this.showError(error);
        } finally {
            this.deleting = false;
        }
    }

    private async reloadProviders(): Promise<void> {
        this.setProviders(await this.aiProvider.getProviders());
    }

    private setProviders(providers: AiProvider[]): void {
        this.providers = providers.map((provider) => ({ ...provider, ai_provider_keyno: String(provider.ai_provider_keyno) }));

        if (!this.providers.some((provider) => provider.ai_provider_keyno === this.page.ai_provider_keyno)) {
            this.page.ai_provider_keyno = '';
            this.replacementApiKey = '';
        }

        this.hasProvidersChange.emit(this.providers.length > 0);
    }

    private clearMessages(): void {
        this.errorMessage = '';
        this.translateErrorMessage = false;
        this.statusMessage = '';
    }

    private showError(error: unknown): void {
        this.translateErrorMessage = error instanceof AiProviderError || !(error instanceof Error);

        if (error instanceof AiProviderError) {
            this.errorMessage = error.translationId;
        } else if (error instanceof Error) {
            this.errorMessage = error.message;
        } else {
            this.errorMessage = 'ai_settings_provider_error';
        }
    }

    private async init(): Promise<void> {
        if (this.busy) return;

        this.loading = true;
        this.errorMessage = '';

        try {
            const [names, providers] = await Promise.all([this.aiProvider.getNames(), this.aiProvider.getProviders()]);
            this.providerNames = names;
            this.setProviders(providers);
        } catch (error: unknown) {
            this.showError(error);
        } finally {
            this.loading = false;
        }
    }

    public async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes['active'] && !this.active) {
            this.page.api_key = '';
            this.replacementApiKey = '';
        }

        if (changes['page']?.currentValue) {
            await this.init();
        }
    }

    public ngOnDestroy(): void {
        if (this.page) this.page.api_key = '';
        this.replacementApiKey = '';
    }
}

interface AiProviderSelection extends AiProvider {
    ai_provider_keyno: string;
}
